import { theme } from '@Guidelines/Theme/theme';
import useCanEditDocument from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useCanEditDocument';
import type { ViewerListAction } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/ViewerListAction';
import FieldLinkCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/Shared/FieldLinkCard';
import type { DocumentEntryView } from '@UI/Store/Documents/Views/buildDocumentEntryView';
import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';
import type DocumentListEntryView from '@UI/Store/Documents/Views/DocumentListEntryView';
import { css } from '@emotion/react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const panelStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const titleStyle = css`
	margin: 0;
	${theme.typography.cardTitle}
	color: ${theme.colors.ink};
`;

const fieldsStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const noteStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

const actionsStyle = css`
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
`;

type Props = {
	readonly list: DocumentListEntryView;
	/** 0-based, an element that exists in `list`. */
	readonly index: number;
	readonly isBusy: boolean;
	readonly onAction: (action: ViewerListAction) => void;
};

type ItemContent = {
	readonly fields: DocumentFieldView[];
	readonly lists: DocumentListEntryView[];
};

const collectItemContent = (entries: readonly DocumentEntryView[], content: ItemContent): void => {
	for (const entry of entries) {
		if (entry.type === 'field') {
			content.fields.push(entry);
		} else if (entry.type === 'group') {
			collectItemContent(entry.entries, content);
		} else {
			content.lists.push(entry);
		}
	}
};

const describeNestedList = (list: DocumentListEntryView): string => {
	if (list.items.length === 0) {
		return `${list.title} : aucun élément.`;
	}

	if (list.items.length === 1) {
		return `${list.title} : 1 élément.`;
	}

	return `${list.title} : ${list.items.length} éléments.`;
};

/**
 * One element of a list: its fields (each a way into its own panel) and the gestures Monter, Descendre,
 * Ajouter après and Supprimer, disabled at the list's bounds and while a call is in flight. The gestures
 * go through `onAction`, the same entry as the viewer's bars.
 */
const ListItemPanel: React.FC<Props> = ({ list, index, isBusy, onAction }) => {
	const canEdit = useCanEditDocument();
	const item = list.items[index];

	const content = React.useMemo(() => {
		const collected: ItemContent = { fields: [], lists: [] };
		collectItemContent(item?.entries ?? [], collected);
		return collected;
	}, [item]);

	const handleMoveUp = React.useCallback(() => {
		onAction({ kind: 'move', listKey: list.key, from: index, to: index - 1 });
	}, [onAction, list.key, index]);

	const handleMoveDown = React.useCallback(() => {
		onAction({ kind: 'move', listKey: list.key, from: index, to: index + 1 });
	}, [onAction, list.key, index]);

	const handleAddAfter = React.useCallback(() => {
		onAction({ kind: 'add', listKey: list.key, position: index + 1 });
	}, [onAction, list.key, index]);

	const handleRemove = React.useCallback(() => {
		onAction({ kind: 'remove', listKey: list.key, index });
	}, [onAction, list.key, index]);

	return (
		<div css={panelStyle}>
			<h3 css={titleStyle}>{`${list.title} ${index + 1} sur ${list.items.length}`}</h3>
			<div css={fieldsStyle}>
				{content.fields.map((field) => (
					<FieldLinkCard
						key={field.key}
						field={field}
						actionLabel={canEdit ? 'Voir les sources et modifier' : 'Voir les sources'}
					/>
				))}
			</div>
			{content.lists.map((nested) => (
				<p key={nested.key} css={noteStyle}>
					{describeNestedList(nested)}
				</p>
			))}
			<ConditionalLoader data={canEdit}>
				<div css={actionsStyle}>
					<Button
						type="button"
						label="Monter"
						icon={<ArrowUp size={16} />}
						severity="secondary"
						outlined
						loading={isBusy}
						disabled={index === 0}
						onClick={handleMoveUp}
					/>
					<Button
						type="button"
						label="Descendre"
						icon={<ArrowDown size={16} />}
						severity="secondary"
						outlined
						loading={isBusy}
						disabled={index === list.items.length - 1}
						onClick={handleMoveDown}
					/>
					<Button
						type="button"
						label="Ajouter après"
						icon={<Plus size={16} />}
						severity="secondary"
						outlined
						loading={isBusy}
						disabled={!list.canAdd}
						onClick={handleAddAfter}
					/>
					<Button
						type="button"
						label="Supprimer"
						icon={<Trash2 size={16} />}
						severity="danger"
						outlined
						loading={isBusy}
						disabled={!list.canRemove}
						onClick={handleRemove}
					/>
				</div>
			</ConditionalLoader>
		</div>
	);
};

export default ListItemPanel;
